import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import type {
  AutofillRequest,
  AutofillResult,
  CityOption,
  CustomerCreated,
  CustomerDraft,
} from '@acs/shared'
import { ApiError, apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api'

/**
 * Data for the create-customer screen.
 *
 * Two things, and they have opposite lifetimes: a catalogue that effectively
 * never changes, and a write that must never be retried.
 */

export const CITIES_KEY = ['catalog', 'cities'] as const
export const AGENT_ROSTER_KEY = ['agents', 'roster'] as const

export interface AgentRosterEntry {
  username: string
  name: string | null
}

/**
 * Every agent, not just the ones who already have customers.
 *
 * Deliberately a different endpoint from the toolbar's `useAgentOptions()`,
 * which is derived from `customers` and therefore cannot list an agent with no
 * customers — the exact agent somebody is most likely to be assigning their
 * first customer to.
 */
export function useAgentRoster(): UseQueryResult<AgentRosterEntry[]> {
  return useQuery({
    queryKey: AGENT_ROSTER_KEY,
    queryFn: ({ signal }) => apiGet<AgentRosterEntry[]>('/api/agents/roster', undefined, signal),
    staleTime: 5 * 60_000,
  })
}

/**
 * The city ⇄ warehouse list.
 *
 * `staleTime: Infinity` because Amazon opening a delivery station is a monthly
 * event and this backs a `<select>` — refetching it on every focus would be a
 * request per tab switch for a list that has not moved since March. The server
 * caches it for ten minutes on top, so even a hard reload is one aggregation
 * per ten minutes across every open console rather than per mount.
 *
 * Prefetched by the rail on hover (`defaultPreload: 'intent'` only covers the
 * route component), so by the time the form paints the list is usually already
 * there and the city picker never shows a spinner.
 */
export function useCities(): UseQueryResult<CityOption[]> {
  return useQuery({
    queryKey: CITIES_KEY,
    queryFn: ({ signal }) => apiGet<CityOption[]>('/api/catalog/cities', undefined, signal),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

/**
 * Create one customer.
 *
 * **`retry: false` is load-bearing, not a default being restated.** This POST
 * allocates two sequence numbers from `meta` before it inserts, and those
 * allocations are not rolled back. A retry on a timeout — where the first
 * request may well have succeeded — would either burn a pair of ids or create
 * the same person twice. The server's unique index on `email_lower` catches the
 * duplicate, but the honest answer is not to send it twice.
 *
 * On success the customer lists are invalidated rather than patched. A patch
 * would have to know where the new row sorts under whatever filter, sort and
 * page the operator has open, and would be wrong for every view except the one
 * that was on screen; the row lands in Pending, which is usually not the view
 * being looked at. One refetch of a 575-document collection is cheaper than the
 * bug. `stats` goes too — the rail's Pending count is part of the feedback that
 * the create worked.
 */
export function useCreateCustomer() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (draft: CustomerDraft) => apiPost<CustomerCreated>('/api/customers', draft),
    retry: false,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['customers'] })
      void qc.invalidateQueries({ queryKey: ['stats'] })
    },
  })
}

/* ------------------------------------------------------------------ *
 * "Fill from message"
 * ------------------------------------------------------------------ */

/**
 * Longer than the server's own 20s ceiling (`AUTOFILL_TIMEOUT_MS`), never
 * shorter: the API always gets to answer with its own sentence, and the
 * client's generic "timed out" is what the operator reads only if even that
 * failed to arrive.
 */
const AUTOFILL_CLIENT_TIMEOUT_MS = 30_000

/**
 * Pasted text in, form values out.
 *
 * One model call per paste, paid and carrying the customer's secrets, so
 * `retry: false` — a failure is shown and the operator decides. The signal
 * lets the dialog cancel a read the operator has abandoned; the server stops
 * its model call in turn.
 */
export function useAutofill() {
  return useMutation({
    mutationFn: ({ text, signal }: AutofillRequest & { signal?: AbortSignal }) =>
      apiPost<AutofillResult>(
        '/api/customers/autofill',
        { text } satisfies AutofillRequest,
        signal,
        AUTOFILL_CLIENT_TIMEOUT_MS
      ),
    retry: false,
  })
}

/* ------------------------------------------------------------------ *
 * Editing, approving and deleting
 *
 * None of the three is optimistic and none retries. Approve and Delete are
 * one-way: moving a row out of Pending before the server agreed means the
 * rollback puts a customer BACK into the list after the operator has moved on,
 * which is more confusing than waiting 300ms for the truth.
 * ------------------------------------------------------------------ */

/** Everything a status change moves between, refreshed together. */
function invalidateLists(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['customers'] })
  // The rail's counts live here. Approving moves one from Pending to Approved,
  // and the two numbers have to change together or neither is believable.
  void qc.invalidateQueries({ queryKey: ['stats'] })
}

/**
 * An absent field is left alone by the server.
 *
 * `computerNumber` is not part of `CustomerDraft` — there is no box for it on
 * the create form and it is not something anyone types when a customer is being
 * set up. It is edited in place in the table, months later, by whoever is
 * tracking that customer. So it rides on the same PATCH and is declared here.
 */
export type CustomerEdit = Partial<CustomerDraft> & { computerNumber?: string | null }

export function useUpdateCustomer(customerId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: CustomerEdit) =>
      apiPatch<{ ok: true }>(`/api/customers/${encodeURIComponent(customerId)}`, patch),
    retry: false,
    onSuccess: () => {
      invalidateLists(qc)
      void qc.invalidateQueries({ queryKey: ['customer', customerId] })
    },
  })
}

export function useApproveCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<{ status: 'approved' }>(`/api/customers/${encodeURIComponent(id)}/approve`),
    retry: false,
    onSuccess: () => invalidateLists(qc),
  })
}

/** The inverse of `useApproveCustomer` — Approved back to Pending. */
export function useUnapproveCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<{ status: 'pending' }>(`/api/customers/${encodeURIComponent(id)}/unapprove`),
    retry: false,
    onSuccess: () => invalidateLists(qc),
  })
}

export function useDeleteCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiDelete<{ deleted: true }>(`/api/customers/${encodeURIComponent(id)}`),
    retry: false,
    onSuccess: () => invalidateLists(qc),
  })
}

/* ------------------------------------------------------------------ *
 * Is this person already on file?
 * ------------------------------------------------------------------ */

export interface ExistingMatch {
  customerId: string
  name: string
  status: string
}

export interface DuplicateReport {
  email: ExistingMatch | null
  phone: ExistingMatch | null
}

const NO_DUPES: DuplicateReport = Object.freeze({ email: null, phone: null })

/**
 * Checks an email and phone against the collection, on demand.
 *
 * `enabled` only once there is something worth asking about, so an empty form
 * makes no requests. Cached per (email, phone) pair for a minute: blurring the
 * same field twice — which happens constantly while tabbing around a long form
 * — must not be a second round trip.
 */
export function useDuplicateCheck(
  email: string,
  phone: string
): UseQueryResult<DuplicateReport> {
  const e = email.trim().toLowerCase()
  const p = phone.trim()
  const ask = e !== '' || p.replace(/\D/g, '').length >= 10

  return useQuery({
    queryKey: ['customers', 'check', e, p] as const,
    queryFn: ({ signal }) =>
      apiGet<DuplicateReport>('/api/customers/check', { email: e, phone: p }, signal),
    enabled: ask,
    staleTime: 60_000,
    // A background lookup that fails must never block the form: the server
    // checks again on submit and the unique index is the real guard.
    retry: false,
    placeholderData: NO_DUPES,
  })
}

/* ------------------------------------------------------------------ *
 * Errors an operator can act on
 * ------------------------------------------------------------------ */

interface FieldIssue {
  field: string
  message: string
}

/** The `details` array the API attaches to 400s and to the create's 409. */
function issuesOf(err: unknown): FieldIssue[] {
  if (!(err instanceof ApiError)) return []
  const body = err.body
  if (typeof body !== 'object' || body === null) return []
  const error = (body as { error?: { details?: unknown } }).error
  const details = error?.details
  if (!Array.isArray(details)) return []
  return details.filter(
    (d): d is FieldIssue =>
      typeof d === 'object' && d !== null && typeof (d as FieldIssue).field === 'string'
  )
}

/** `{ email: 'Already on file' }` — ready to merge into the form's own errors. */
export function fieldErrorsOf(err: unknown): Record<string, string> {
  const out: Record<string, string> = {}
  for (const issue of issuesOf(err)) {
    if (out[issue.field] === undefined) out[issue.field] = issue.message
  }
  return out
}

/**
 * What to show a human.
 *
 * The raw message is usually the right thing — the API writes them for
 * operators, and "That email already belongs to Arpan (admin000079), who is
 * confirmed" is more useful than anything generic. These are the cases where it
 * is not: a status code with no useful body, or a transport failure where the
 * `Error` says "Failed to fetch".
 */
export function friendlyError(err: unknown): string {
  if (!(err instanceof ApiError)) {
    return 'Could not reach the server. Check your connection and try again.'
  }

  switch (err.status) {
    case 401:
      return 'Your session has expired. Sign in again, then resubmit — nothing was saved.'
    case 403:
      return 'You do not have permission to create customers.'
    case 429:
      return 'Too many requests. Wait a moment and try again.'
    case 503:
      return 'The database is unreachable right now. Nothing was saved; try again shortly.'
    default:
      break
  }

  if (err.message !== '') return err.message
  return err.status >= 500
    ? 'The server failed while saving. Nothing was created — try again.'
    : 'The form could not be submitted.'
}
